import React, {useContext, useEffect, useMemo, useState} from "react";
import Markdown, {Components} from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {CodeBlock} from "./blocks/CodeBlock";
import {useAnimatedText} from "../../hooks";
import {Citation} from "./inline/Citation";
import {ActionIcon, Anchor, Box, Modal, Stack, Table, Text} from "@mantine/core";
import classes from "./GPTStreamParser.module.css";
import {useDebouncedValue} from "@mantine/hooks";
import {processReferences, stripLCitations, escapeCitationBrackets, fixNestedMarkdownLinks} from "./utils";
import {ParagraphWithReferences} from "./inline/ParagraphWithReferences";
import ExternalLinkIcon from "../../../../assets/external-link.svg?react";
import ArrowsMaximizeIcon from "../../../../assets/arrows-maximize.svg?react";
import XIcon from "../../../../assets/x.svg?react";
import {useTranslation} from "react-i18next";
import {GPTMeta, GPTMetaContext} from "./MetaProvider";
import {useNavigate} from "react-router-dom";
import {useExperimentalLinkResolution} from "../../../../components/ExperimentalLink";

// Custom components for the special tags

export const Thinking: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            m={0}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            <Text fz="md" c="gray.6" m={0}>
                {processReferences(children)}
            </Text>
        </Box>
    );
};

export const Step: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            <Text fz="md" c="gray.6" m={0}>
                {processReferences(children)}
            </Text>
        </Box>
    );
};

export const Count: React.FC<any> = ({children, ...props}) => {
    return (
        <div
            style={{
                backgroundColor: "#fff5e6",
                padding: "5px",
                borderRadius: "4px",
                color: "#ff6600",
                display: "none",
            }}
        >
            {children}
        </div>
    );
};

export const Reflection: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            {processReferences(children)}
        </Box>
    );
};

export const Answer: React.FC<any> = ({children, ...props}) => {
    return <div>{processReferences(children)}</div>;
};

export const Reward: React.FC<any> = ({children, ...props}) => {
    return (
        <div
            style={{
                backgroundColor: "#f0f8ff",
                padding: "5px",
                borderRadius: "4px",
                fontStyle: "italic",
                display: "none",
            }}
        >
            {children}
        </div>
    );
};

// Custom link component for app.dib.no URLs
const DibAppLink: React.FC<{href: string; children: React.ReactNode}> = ({
    href,
    children,
}) => {
    const context = useContext(GPTMetaContext);
    const navigate = useNavigate();
    const {effectiveHref, internalPath} = useExperimentalLinkResolution(href);
    const isDibAppLink =
        effectiveHref?.includes("app.dib.no") ||
        effectiveHref?.startsWith("/dibappapi/");

    const toolTitle = useMemo(() => {
        if (
            context?.relatedTools === undefined ||
            context?.relatedTools.length === 0
        ) {
            return undefined;
        }
        for (const tool of context?.relatedTools) {
            if (tool.url === href) {
                return tool.title;
            }
        }
        return undefined;
    }, [context?.relatedTools]);

    const usedSourceTitle = useMemo(() => {
        if (context?.sources === undefined || context?.sources.length === 0) {
            return undefined;
        }
        for (const source of context?.sources) {
            if (href.includes(source.url)) {
                return source.name;
            }
        }
        return undefined;
    }, [context?.sources]);

    const consideredSourceTitle = useMemo(() => {
        if (
            context?.consideredSources === undefined ||
            context?.consideredSources.length === 0
        ) {
            return undefined;
        }
        for (const source of context?.consideredSources) {
            if (href.includes(source.url)) {
                return source.name;
            }
        }
        return undefined;
    }, [context?.sources]);

    // Differentiate between plain links without title and titled markdown links
    // Plain links will be an array with href at index 0
    const altTitle =
        Array.isArray(children) && children.length > 0 && href === children[0]
            ? "app.dib"
            : children;

    const displayTitle =
        toolTitle || usedSourceTitle || consideredSourceTitle || altTitle;

    if (isDibAppLink && internalPath) {
        return (
            <Anchor
                href={internalPath}
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate(internalPath, {viewTransition: true});
                }}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                }}
            >
                <span>{displayTitle}</span>
            </Anchor>
        );
    }

    if (isDibAppLink) {
        return (
            <Anchor
                href={effectiveHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                }}
            >
                <span>{displayTitle}</span>
                <ExternalLinkIcon width={16} height={16} />
            </Anchor>
        );
    }

    return (
        <Anchor href={effectiveHref} target="_blank" rel="noopener noreferrer">
            {children}
        </Anchor>
    );
};

type Props = {
    text: string;
    meta: GPTMeta;
    /**
     * The time in milliseconds between each update
     */
    delta?: number;
    className?: string;
    hideCitations?: boolean;
    options?: {
        includeCustomComponents?: {
            thinking?: boolean;
            step?: boolean;
            count?: boolean;
            reflection?: boolean;
            answer?: boolean;
            reward?: boolean;
            code?: boolean;
            references?: boolean;
        };
        chunkSize?: number;
    };
    onFinish?: () => void;
};

// A wrapped component generator used for most markdown elements that must have references processed.
const createWrappedComponent = (Component: React.ElementType) => {
    return ({node, children, ...props}: any) => {
        const processedChildren = processReferences(children);
        return <Component {...props}>{processedChildren}</Component>;
    };
};

// Custom table components using Mantine's Table for better styling
const CustomTable: React.FC<any> = ({node, children, ...props}) => {
    const [expanded, setExpanded] = useState(false);
    const {t} = useTranslation();

    const tableContent = (
        <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            className={classes.table}
            {...props}
        >
            {children}
        </Table>
    );

    return (
        <>
            <Box className={classes.tableWrapper}>
                {tableContent}
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    className={classes.expandButton}
                    onClick={() => setExpanded(true)}
                    title={t("table.expand")}
                >
                    <ArrowsMaximizeIcon width={14} height={14} />
                </ActionIcon>
            </Box>

            <Modal
                opened={expanded}
                onClose={() => setExpanded(false)}
                size="xl"
                fullScreen
                classNames={{
                    body: classes.modalBody,
                    content: classes.modalContent,
                }}
                withCloseButton={false}
            >
                <Box
                    className={classes.expandedTableContainer}
                    onClick={() => setExpanded(false)}
                >
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        className={classes.closeButton}
                        onClick={() => setExpanded(false)}
                        title={t("table.close")}
                    >
                        <XIcon width={18} height={18} />
                    </ActionIcon>
                    <Box
                        className={classes.expandedTableScroll}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                            withColumnBorders
                            className={classes.expandedTable}
                            {...props}
                        >
                            {children}
                        </Table>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

const CustomThead: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Thead {...props}>{children}</Table.Thead>;
};

const CustomTbody: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Tbody {...props}>{children}</Table.Tbody>;
};

const CustomTr: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Tr {...props}>{children}</Table.Tr>;
};

const CustomTh: React.FC<any> = ({node, children, ...props}) => {
    const processedChildren = processReferences(children);
    return <Table.Th {...props}>{processedChildren}</Table.Th>;
};

const CustomTd: React.FC<any> = ({node, children, ...props}) => {
    const processedChildren = processReferences(children);
    return <Table.Td {...props}>{processedChildren}</Table.Td>;
};

/**
 * CustomParagraph checks if its sole child is one of our custom elements.
 * If so, it returns that element directly (so that it isn't wrapped in <p></p>).
 * Otherwise, it falls back to a normal paragraph.
 */
const CustomParagraph: React.FC<any> = ({node, children, ...props}) => {
    const childArray = React.Children.toArray(children);

    // If the paragraph contains exactly one child AND that child is one of our custom tags,
    // then return it directly
    if (childArray.length === 1 && React.isValidElement(childArray[0])) {
        const element = childArray[0];
        const type = element.type;

        // Check against both string types and our imported component references.
        if (
            type === "thinking" ||
            type === Thinking ||
            type === "step" ||
            type === Step ||
            type === "count" ||
            type === Count ||
            type === "reflection" ||
            type === Reflection ||
            type === "answer" ||
            type === Answer ||
            type === "reward" ||
            type === Reward ||
            type === DibAppLink
        ) {
            return <>{element}</>;
        }
    }

    // Otherwise, render a normal <p> with processed references.
    return <ParagraphWithReferences>{children}</ParagraphWithReferences>;
};

const Nada = () => <></>;


// Helper function to remove trailing incomplete tags
const stripUnclosedCustomTags = (text: string): string => {
    if (!text) {
        return text;
    }
    let result = text;
    // Loop until there's no unmatched "<" at the end of the string.
    while (true) {
        const lastOpenIndex = result.lastIndexOf("<");
        // If an opening bracket exists and there is no closing ">" after it,
        // assume this is an incomplete tag and trim the string up to that point.
        if (lastOpenIndex !== -1 && result.indexOf(">", lastOpenIndex) === -1) {
            result = result.substring(0, lastOpenIndex);
        } else {
            break;
        }
    }
    return result;
};

// Main component that processes and renders markdown
export const GPTStreamParser: React.FC<Props> = ({
    text,
    meta,
    delta = 5,
    className,
    hideCitations,

    options = {
        chunkSize: 3,
        includeCustomComponents: {
            thinking: true,
            step: true,
            count: true,
            reflection: true,
            answer: true,
            reward: true,
            code: true,
            references: true,
        },
    },
    onFinish,
}) => {
    const displayText = useAnimatedText(text, {
        delta,
        chunkSize: 3,
        adaptive: true,
    });

    // Debug
    /* const [debouncedText] = useDebouncedValue(text, 100);
    useEffect(() => {
        if (debouncedText) {
            console.log(`Base text before parsing: \n${debouncedText}`);
        }
    }, [debouncedText]); */

    useEffect(() => {
        if (!onFinish) {
            return;
        }

        if (text && displayText.length === text.length) {
            onFinish();
        }
    }, [onFinish, displayText, text]);

    return (
        <Box className={`${classes.container} ${className} ${hideCitations ? classes.hideCitations : ""}`}>
            <GPTMetaContext.Provider value={meta}>
                <Markdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={
                        {
                            li: createWrappedComponent("li"),
                            // Use our custom paragraph to "unwrap" custom blocks if detected.
                            p: CustomParagraph,
                            /* p: createWrappedComponent("p"), */
                            strong: createWrappedComponent("strong"),
                            em: createWrappedComponent("em"),
                            blockquote: createWrappedComponent("blockquote"),
                            // Custom table components with Mantine styling and reference processing
                            table: CustomTable,
                            thead: CustomThead,
                            tbody: CustomTbody,
                            tr: CustomTr,
                            th: CustomTh,
                            td: CustomTd,
                            // Map our custom HTML tags to the appropriate components.
                            references: options.includeCustomComponents
                                ?.references
                                ? CodeBlock
                                : Nada,
                            thinking: options.includeCustomComponents?.thinking
                                ? Thinking
                                : Nada,
                            step: options.includeCustomComponents?.step
                                ? Step
                                : Nada,
                            count: options.includeCustomComponents?.count
                                ? Count
                                : Nada,
                            reflection: options.includeCustomComponents
                                ?.reflection
                                ? Reflection
                                : Nada,
                            finalreflection: options.includeCustomComponents
                                ?.reflection
                                ? Reflection
                                : Nada,
                            answer: options.includeCustomComponents?.answer
                                ? Answer
                                : Nada,
                            reward: options.includeCustomComponents?.reward
                                ? Reward
                                : Nada,
                            a: DibAppLink,
                        } as Components
                    }
                >
                    {escapeCitationBrackets(fixNestedMarkdownLinks(stripLCitations(stripUnclosedCustomTags(displayText || text))))}
                </Markdown>
            </GPTMetaContext.Provider>
        </Box>
    );
};
